import { Suspense, useMemo, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { Draft, DraftFileEntry, Problem } from '../api/types';
import { CodeViewerLazy } from '../code/CodeViewerLazy';
import { FileTree, pickFile } from '../code/FileTreeView';
import { MANIFEST } from '../code/fileTree';
import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { usePlatform } from '../app/platform';
import { toProblem, useAsync } from '../app/useAsync';

export interface DraftEditorProps {
  workspaceId: string;
  appId: string;
  /** 表示中の Draft。head とファイル一覧はこれを正にする。 */
  draft: Draft;
  /** URLで指定されているファイル。無ければ manifest.json を開く。 */
  file: string | null;
  onSelectFile(path: string): void;
  /** 保存・削除の応答で受け取った新しい Draft。呼び出し側は取り直さずにこれを使う。 */
  onDraftChange(draft: Draft): void;
  /** 競合のあとに Draft を取り直させる。 */
  onReload(): void;
}

const CONFLICT_MESSAGE = 'ほかの変更で版が進みました。最新を読み込んでから編集し直してください。';
const decoder = new TextDecoder();

/**
 * Draft のファイルの表示と直接編集（計画の調整 B2・B20）。
 *
 * 本文はファイルを開くたびに 1 つ取る（GET A/draft/files/{path}）。テキストだけを textarea で編集し、
 * 保存は編集を始めた時点の head を If-Match に付けて送る。その後に版が進んでいれば API が 409 を返す（B5）。
 */
export function DraftEditor({
  workspaceId,
  appId,
  draft,
  file,
  onSelectFile,
  onDraftChange,
  onReload,
}: DraftEditorProps) {
  const paths = useMemo(() => draft.files.map((entry) => entry.path), [draft.files]);
  if (paths.length === 0) return <EmptyState title="Draft にファイルがありません" />;

  const current = pickFile(paths, file);
  const entry = draft.files.find((item) => item.path === current);
  const hidden = draft.files.filter((item) => !isText(item)).length;

  return (
    <div className="code-layout">
      <FileTree paths={paths} current={current} onSelectFile={onSelectFile} />
      <section className="code-editor">
        {entry && (
          <DraftFilePane
            // ファイルを切り替えたら、編集中の状態を持ち越さない。
            key={entry.path}
            workspaceId={workspaceId}
            appId={appId}
            headRevisionId={draft.headRevisionId}
            entry={entry}
            onDraftChange={onDraftChange}
            onReload={onReload}
          />
        )}
        <div className="code-footer">
          Draft のファイルを表示しています。保存すると新しい版になります。
          {hidden > 0 && `　·　画像など表示できないファイル ${hidden} 件`}
        </div>
      </section>
    </div>
  );
}

interface DraftFilePaneProps {
  workspaceId: string;
  appId: string;
  headRevisionId: string;
  entry: DraftFileEntry;
  onDraftChange(draft: Draft): void;
  onReload(): void;
}

function DraftFilePane({
  workspaceId,
  appId,
  headRevisionId,
  entry,
  onDraftChange,
  onReload,
}: DraftFilePaneProps) {
  const { api } = usePlatform();
  const text = isText(entry);
  // 本文は path と sha256 を鍵に取る。保存やほかのタブの変更で中身が変わったときだけ取り直す。画像は取らない。
  const content = useAsync(
    () =>
      text
        ? api.getDraftFile(workspaceId, appId, entry.path).then((loaded) => decoder.decode(loaded.bytes))
        : Promise.resolve(''),
    [api, workspaceId, appId, entry.path, entry.sha256, text],
  );
  // base は編集を始めた時点の head。保存の If-Match に使い、編集中に進んだ版を黙って上書きしない。
  const [editing, setEditing] = useState<{ base: string; value: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [deleting, setDeleting] = useState(false);
  // 保存の冪等キー。本文が前回の保存の試みと同じなら同じキーで再送し（応答だけ失われた再送を
  // 重複排除する）、本文を変えたら作り直す（サーバーの指紋は ifMatch と本文の sha256。F4）。
  const [saveIdempotency, setSaveIdempotency] = useState<{ key: string; value: string } | null>(null);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setProblem(null);
    const idempotencyKey =
      saveIdempotency !== null && saveIdempotency.value === editing.value
        ? saveIdempotency.key
        : newIdempotencyKey();
    setSaveIdempotency({ key: idempotencyKey, value: editing.value });
    try {
      const mutation = await api.putDraftFile(workspaceId, appId, entry.path, editing.value, {
        headRevisionId: editing.base,
        idempotencyKey,
      });
      setEditing(null);
      setSaveIdempotency(null);
      onDraftChange(mutation.draft);
    } catch (error) {
      const failure = toProblem(error);
      // 競合は入力を残したまま専用の表示にする。ほかの拒否（422 など）は理由をそのまま出す。
      if (failure.code === 'revision_conflict') setConflict(true);
      else setProblem(failure);
    } finally {
      setSaving(false);
    }
  };

  const stopEditing = () => {
    setEditing(null);
    setConflict(false);
    setProblem(null);
    setSaveIdempotency(null);
  };

  return (
    <>
      <div className="code-bar">
        <code>{entry.path}</code>
        <span className="code-bar-actions">
          {editing ? (
            <>
              <Button variant="primary" disabled={saving} onClick={() => void save()}>
                保存する
              </Button>
              <Button disabled={saving} onClick={stopEditing}>
                編集をやめる
              </Button>
            </>
          ) : (
            <>
              {text && (
                <Button
                  // 取り直しの間（content.loading）も無効にする。保存直後や SSE での Draft の
                  // 取り直し中に押すと、textarea は前の版の本文なのに If-Match は新しい head になり、
                  // 直前の変更を黙って上書きしてしまう（レビュー指摘 F1）。
                  disabled={content.data === null || content.loading}
                  onClick={() => setEditing({ base: headRevisionId, value: content.data ?? '' })}
                >
                  編集する
                </Button>
              )}
              {/* manifest.json は Draft から外せない（API は 422 protected_path。計画の調整 B19）。 */}
              {entry.path !== MANIFEST && (
                <Button variant="text" onClick={() => setDeleting(true)}>
                  このファイルを削除
                </Button>
              )}
            </>
          )}
        </span>
      </div>
      {conflict && (
        <div className="form-conflict" role="alert">
          <span>{CONFLICT_MESSAGE}</span>
          <div className="row">
            <Button
              onClick={() => {
                stopEditing();
                onReload();
              }}
            >
              最新を読み込む
            </Button>
          </div>
        </div>
      )}
      <ProblemNotice problem={problem} />
      {!text ? (
        <div className="code-scroll draft-binary">
          <p className="hint">{`画像は表示しません（${formatBytes(entry.size)}）`}</p>
        </div>
      ) : editing ? (
        <textarea
          className="draft-textarea"
          aria-label={`${entry.path} を編集`}
          value={editing.value}
          disabled={saving}
          spellCheck={false}
          onChange={(event) => {
            const value = event.target.value;
            setEditing((edit) => (edit ? { ...edit, value } : edit));
          }}
        />
      ) : content.loading ? (
        // 取り直し中（content.loading）は本文の代わりに出す。表示中の本文と押せる「編集する」を
        // 揃え、古い本文が新しい head と組み合わさって見えないようにする（F1）。前回の失敗の表示より先に判定する。
        <Spinner label="ファイルの中身を取得しています" />
      ) : content.problem ? (
        <ProblemNotice problem={content.problem} action={<Button onClick={content.reload}>再取得</Button>} />
      ) : content.data === null ? (
        <Spinner label="ファイルの中身を取得しています" />
      ) : (
        <Suspense fallback={<Spinner label="表示を準備しています" />}>
          <CodeViewerLazy path={entry.path} content={content.data} />
        </Suspense>
      )}
      {deleting && (
        <ConfirmDialog
          title={`「${entry.path}」を削除`}
          body={<p>このファイルを Draft から外した新しい版を作ります。版の履歴の「この版に戻す」で戻せます。</p>}
          confirmLabel="削除する"
          describeFailure={(failure) => (failure.code === 'revision_conflict' ? CONFLICT_MESSAGE : null)}
          onConfirm={async (idempotencyKey) => {
            const mutation = await api.deleteDraftFile(workspaceId, appId, entry.path, {
              headRevisionId,
              idempotencyKey,
            });
            setDeleting(false);
            onDraftChange(mutation.draft);
          }}
          onClose={() => setDeleting(false)}
        />
      )}
    </>
  );
}

/** 画像（image/*）以外はテキストとして表示・編集する。API の draft_paths.is_text と同じ区分。 */
function isText(entry: DraftFileEntry): boolean {
  return !entry.mediaType.startsWith('image/');
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}
